import axios from 'axios';
import { ref } from 'vue';

/**
 * Service for policy search operations
 */
export const usePolicySearchService = () => {
  // State
  const isLoading = ref(false);
  const error = ref(null);
  const searchResults = ref([]);
  
  /**
   * Search policies by phone number
   * @param {string} phoneNumber - The phone number to search by
   * @returns {Promise} Promise that resolves with search results
   */
  const searchByPhone = async (phoneNumber) => {
    if (!phoneNumber) {
      error.value = 'Phone number is required';
      return [];
    }
    
    // Clean the phone number by removing parentheses and hyphens
    const cleanedPhoneNumber = phoneNumber.replace(/[()\s-]+/g, '');
    
    return searchPolicies({ phoneNumber: cleanedPhoneNumber });
  };
  
  /**
   * Search policies by email
   * @param {string} email - The email to search by
   * @returns {Promise} Promise that resolves with search results
   */
  const searchByEmail = async (email) => {
    if (!email) {
      error.value = 'Email is required';
      return [];
    }
    
    return searchPolicies({ email });
  };
  
  /**
   * Search policies by address
   * @param {Object} addressData - The address to search by
   * @returns {Promise} Promise that resolves with search results
   */
  const searchByAddress = async (addressData) => {
    if (!addressData.address) {
      error.value = 'Address is required';
      return [];
    }
    
    return searchPolicies({ address: addressData });
  };
  
  /**
   * Search policies by advanced criteria
   * @param {Object} criteria - The search criteria
   * @returns {Promise} Promise that resolves with search results
   */
  const searchByAdvanced = async (criteria) => {
    return searchPolicies({ advanced: criteria });
  };
  
  /**
   * Search policies by policy number
   * @param {string} policyNumber - The policy number to search by
   * @returns {Promise} Promise that resolves with search results
   */
  const searchByPolicyNumber = async (policyNumber) => {
    if (!policyNumber) {
      error.value = 'Policy number is required';
      return [];
    }
    
    return searchPolicies({ policyNumber });
  };
  
  /**
   * Search policies by customer name
   * @param {string} customerName - The customer name to search by
   * @returns {Promise} Promise that resolves with search results
   */
  const searchByCustomerName = async (customerName) => {
    if (!customerName) {
      error.value = 'Customer name is required';
      return [];
    }
    
    return searchPolicies({ customerName });
  };
  
  /**
   * Base method to search policies
   * @param {Object} payload - The search payload
   * @returns {Promise} Promise that resolves with search results
   */
  const searchPolicies = async (payload) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      // Make the API call
      const response = await axios.post('/api/verify-policy', payload);
      
      // Check if we have policy info
      if (response.data && response.data.policyInfo) {
        searchResults.value = response.data.policyInfo;
        return response.data.policyInfo;
      }
      
      // No results
      searchResults.value = [];
      return [];
    } catch (err) {
      console.error('Policy search error:', err);
      error.value = err.response
        ? err.response.data.error
        : 'An error occurred while searching for policies';
      
      searchResults.value = [];
      return [];
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Format a date string to display format
   * @param {string} dateStr - The date string to format
   * @returns {string} Formatted date string
   */
  const formatDateString = (dateStr) => {
    if (!dateStr) return '';
    
    const dateObj = new Date(dateStr);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${month}-${day}-${year}`;
  };
  
  return {
    isLoading,
    error,
    searchResults,
    searchByPhone,
    searchByEmail,
    searchByAddress,
    searchByAdvanced,
    searchByPolicyNumber,
    searchByCustomerName,
    formatDateString
  };
}; 